<template>
  <div class="car-container">
    <dl>
      <div class="card-header">
        <dd class="image-wrapper">
          <img :src="imageUrl" :alt="displayName" class="car-image" loading="lazy" />
        </dd>
        <dt class="title-wrapper">{{ displayName }}</dt>
        <dd class="spec-row price" :class="{ match: matchingItems.includes('price') }">
          <span>{{ i18n.n(price, 'currency') }}</span>
          <span v-if="matchingItems.includes('price')" class="check-bubble">
            <icon icon="check" :width="12" :height="12" color="#ffffff" />
          </span>
        </dd>
        <dd class="kom-number-row">{{ komNumber }}</dd>
      </div>
      <dd class="spec-group spec-row engine" :class="{ match: matchingItems.includes('engine') }">
        <span>{{ engine }}</span>
        <span v-if="matchingItems.includes('engine')" class="check-bubble">
          <icon icon="check" :width="12" :height="12" color="#ffffff" />
        </span>
      </dd>
      <dd class="spec-group spec-row" :class="{ match: matchingItems.includes('colorCode') }">
        <span>{{ colorCode }}</span>
        <span v-if="matchingItems.includes('colorCode')" class="check-bubble">
          <icon icon="check" :width="12" :height="12" color="#ffffff" />
        </span>
      </dd>
      <dd class="spec-group spec-row" :class="{ match: matchingItems.includes('interiorCode') }">
        <span>{{ interiorCode }}</span>
        <span v-if="matchingItems.includes('interiorCode')" class="check-bubble">
          <icon icon="check" :width="12" :height="12" color="#ffffff" />
        </span>
      </dd>
      <dd v-if="options.length" class="options-header">
        {{ i18n.t('/stockmatch/options-header') }}
      </dd>
      <dd
        v-for="option in options"
        :key="option.shortId"
        class="spec-row spec-group"
        :class="{
          option: option.shortId.substring(0, 7) !== 'missing',
          match: matchingItems.includes(`option-${option.shortId}`),
          additional:
            option.type === stockMatchOptionType.additional ||
            option.type === stockMatchOptionType.aditional,
        }"
      >
        <span>{{
          option.shortId.substring(0, 7) === 'missing' ? '-' : `${option.shortId} - ${option.name}`
        }}</span>
        <span v-if="matchingItems.includes(`option-${option.shortId}`)" class="check-bubble">
          <icon icon="check" :width="12" :height="12" color="#ffffff" />
        </span>
      </dd>
    </dl>
    <div class="action-buttons">
      <app-button data-qa="stockcar-detail-button" @click="$emit('stockcar-details')">
        {{ i18n.t('/stockmatch/stockcardetailbutton') }}
      </app-button>
      <app-button data-qa="reservation-status-button" @click="$emit('reservation-status')">
        {{ i18n.t('/stockmatch/reservationstatusbutton') }}
      </app-button>
      <app-button data-qa="new-quote-button" @click="$emit('select-car')">
        {{ i18n.t('/stockmatch/newquotebutton') }}
      </app-button>
    </div>
  </div>
</template>

<script>
import AppButton from '@/components/generic/presentational/atoms/appButton/AppButton.vue'
import Icon from '@/components/generic/presentational/atoms/icon/Icon.vue'
import stockMatchOptionType from '@/helpers/enums/stockMatchOptionType'
export default {
  components: {
    AppButton,
    Icon,
  },
  props: {
    imageUrl: {
      type: String,
      // default is a 3:2 grey area
      default:
        'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAMAAAACCAQAAAA3fa6RAAAAEElEQVR42mM8858BCBghFAAgNgOZPTV32QAAAABJRU5ErkJggg==',
    },
    displayName: {
      type: String,
      required: true,
    },
    price: {
      type: Number,
      required: true,
    },
    engine: {
      type: String,
      default: '',
    },
    transmission: {
      type: String,
      default: '',
    },
    colorCode: {
      type: String,
      default: '',
    },
    interiorCode: {
      type: String,
      default: '',
    },
    options: {
      type: Array,
      default: () => [],
    },
    isFullMatch: {
      type: Boolean,
      default: false,
    },
    matchingItems: {
      type: Array,
      default: () => [],
    },
    carStatus: {
      type: String,
      default: '',
    },
    komNumber: {
      type: String,
      default: '',
    },
  },
  emits: ['select-car', 'stockcar-details', 'reservation-status'],
  data() {
    return {
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
      stockMatchOptionType: stockMatchOptionType,
    }
  },
}
</script>

<style lang="scss" scoped>
.car-container {
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

// Image area
.image-wrapper {
  margin: 0;

  .car-image {
    width: 100%;
    display: block;
    aspect-ratio: 3 / 2;
    object-fit: contain;
  }
}

// All non-image content gets consistent horizontal padding
dl > *:not(.image-wrapper) {
  padding-left: 1rem;
  padding-right: 0.75rem;
}

// CST-4439 keep 4: the card identity (image + name + price + komnr) is grouped
// so it can be frozen at the top while the spec/option rows scroll underneath.
// The sticky behaviour itself is applied only by the stock-match modal (see
// StockMatchContainer.vue `:deep(.card-header)`), so inline usages of this card
// are unaffected. Here we only keep the wrapper full-bleed (image edge-to-edge)
// while its inner non-image rows retain the standard horizontal padding.
.car-container .card-header {
  padding: 0;

  > *:not(.image-wrapper) {
    padding-left: 1rem;
    padding-right: 0.75rem;
  }
}

// Title
.title-wrapper {
  font-size: 1.25rem;
  font-weight: $font-weight-bold;
  margin-top: 0.75rem;
  margin-bottom: 0.25rem;
  padding-left: 1rem;
  padding-right: 0.25rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

// Price
.spec-row.price {
  font-size: 1rem;
  font-weight: $font-weight-bold;
  margin-bottom: 0.15rem;
}

// Kom number
.kom-number-row {
  font-size: 0.8rem;
  color: $gray-darker;
  margin-bottom: 0.75rem;
  padding-left: 1rem;
}

// Spec rows (engine / color / interior / options)
.spec-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.85rem;
  line-height: 1.6;
  padding-right: 0.75rem;

  > span:first-child {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &.match > span:first-child {
    font-weight: $font-weight-bold;
  }
}

// Section dividers
.spec-group {
  border-top: 1px solid $gray-light;
  padding-top: 0.4rem;
  margin-top: 0.2rem;
}

// "Opties" label
.options-header {
  font-weight: $font-weight-bold;
  font-size: 0.85rem;
  margin-top: 2rem;
  padding-bottom: 0.15rem;
}

// Green checkmark bubble
.check-bubble {
  background: $color-success;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.2em;
  height: 1.2em;
  flex-shrink: 0;
  margin-left: 0.4rem;
}

// Highlight options that are in stock but not in config
dd.additional {
  background-color: $color-primary-light;
}

// Full match badge (hidden when not a full match)
.full-match-container div.full-match {
  display: inline-block;
  color: $white;
  background: $color-success;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 0.75rem;
  font-weight: $font-weight-bold;
  margin-bottom: 0.5rem;
}

// Action buttons
.action-buttons {
  padding: 1rem 1rem 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  :deep(button) {
    width: 100%;
    margin: 0;
  }
}
</style>
